import '#lit/components/journeys/match_field_row/index.js';
import type {
  AudienceExportSchedule,
  ConsentType,
  ExportConfigFormFieldTypeMap,
} from '#lit/types/audience_exports.js';
import {
  CAMPAIGN_TYPE,
  ExportConfigFormField,
  ExportInclusionCriteria,
} from '#lit/types/audience_exports.js';
import {AudienceSyncFrequencies} from '#lit/types/audiences.js';
import {ByType} from '#lit/types/journey/schedule.js';
import {
  AudienceFrequencyEnum,
  AudienceScheduleEndTypeEnum,
  AudienceScheduleStartTypeEnum,
} from '#lit/types/journey/audience_frequency.js';
import type {Model as ExportFormModel} from './architecture.js';
import {WeekdaysEnum} from '#lit/common/constants/dates.js';
import {fetcher} from '#lit/utils/http.js';
import {RequestMethod} from '#lit/common/constants/index.js';
import type {FormSelectFieldOption} from '#lit/components/base/form_select_field/index.js';
import type {Dataset} from '#lit/types/datasets.js';
import {
  QualtricsListType,
  MarketoAudienceType,
  SailthruApiType,
} from '#lit/components/audiences/audience_export_form/constants.js';
import {locale} from '#lit/common/locale/index.js';
import {DestinationLocale} from '#lit/common/locale/destination.js';
import {html} from 'lit';

export const SELECT_ALL_FIELDS_EVENT = 'selectAllFields';

export type AudienceExportFormValues = Pick<
  ExportConfigFormFieldTypeMap,
  | ExportConfigFormField.EXPORT_NAME
  | ExportConfigFormField.CAMPAIGN_TYPE
  | ExportConfigFormField.EXPORT_FREQUENCY
  | ExportConfigFormField.SYNC_FREQUENCY
  | ExportConfigFormField.SYNC_END
  | ExportConfigFormField.PERSONALIZATION_FIELDS
  | ExportConfigFormField.END_DATE
  | ExportConfigFormField.VARIATION
  | ExportConfigFormField.AUDIENCE_FOLDER
  | ExportConfigFormField.AUDIENCE_EXPORT_CAMPAIGN_ID
  | ExportConfigFormField.PLATFORM_TYPE
  | ExportConfigFormField.SCHEDULE
  | ExportConfigFormField.API_TYPE
  | ExportConfigFormField.EXPORT_INCLUSION_CRITERIA
  | ExportConfigFormField.ADDITIONAL_FIELDS
  | ExportConfigFormField.REP_NAME
  | ExportConfigFormField.AUDIENCE_SEGMENT_ID
  | ExportConfigFormField.USE_TYPE
  | ExportConfigFormField.BILLING_GROUP_ID
  | ExportConfigFormField.TARGET_DELIVERY_DATE
  | ExportConfigFormField.SEND_DATE
  | ExportConfigFormField.DESCRIPTION
  | ExportConfigFormField.VIANT_SEGMENT_NAME
  | ExportConfigFormField.VIANT_SEGMENT_ID
  | ExportConfigFormField.VIANT_SEGMENT_ID_TYPE
  | ExportConfigFormField.VIANT_AID
  | ExportConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED
  | ExportConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED
  | ExportConfigFormField.NEW_OR_EXISTING_CAMPAIGN
  | ExportConfigFormField.CAMPAIGN_ID
  | ExportConfigFormField.CAMPAIGN_NAME
  | ExportConfigFormField.CONTACT_LIST_NAME
  | ExportConfigFormField.MARKETING_CLOUD_BUSINESS_UNIT_ID
  | ExportConfigFormField.MARKETING_CLOUD_FOLDER
  | ExportConfigFormField.PULSE_POINT_ADVERTISER
  | ExportConfigFormField.PULSE_POINT_PRODUCT
  | ExportConfigFormField.QUALTRICS_LIST_TYPE
  | ExportConfigFormField.QUALTRICS_DIRECTORY_ID
  | ExportConfigFormField.MARKETO_AUDIENCE_TYPE
  | ExportConfigFormField.MARKETO_CUSTOM_OBJECT
  | ExportConfigFormField.DV360_APP_ID
  | ExportConfigFormField.GOOGLE_ADS_APP_ID
  | ExportConfigFormField.AAM_DATA_SOURCE_ID
  | ExportConfigFormField.AEP_DATASET_ID
  | ExportConfigFormField.AEP_CUSTOM_NAMESPACE
  | ExportConfigFormField.TABLE_NAME
  | ExportConfigFormField.USER_CONSENT
  | ExportConfigFormField.USER_DATA_CONSENT
  | ExportConfigFormField.USER_PERSONALIZATION_CONSENT
  | ExportConfigFormField.ACTIVATED_DESTINATIONS
  | ExportConfigFormField.LIMIT
  | ExportConfigFormField.SORT_BY
  | ExportConfigFormField.SORT_DIRECTION
  | ExportConfigFormField.SELECTED_DATASET
>;

export type BaseConfigOptions = Partial<{
  defaultExportName: string;
  includeSchedule: boolean;
  defaultTableName: string;
  userConsent: string;
  userDataConsent: string;
  userPersonalizationConsent: string;
}>;

export function generateDefaultBaseConfig(
  opts: BaseConfigOptions = {}
): AudienceExportFormValues {
  return {
    [ExportConfigFormField.PLATFORM_TYPE]: '',
    [ExportConfigFormField.CAMPAIGN_TYPE]: CAMPAIGN_TYPE.ONGOING,
    [ExportConfigFormField.SYNC_FREQUENCY]: AudienceSyncFrequencies.DAILY,
    [ExportConfigFormField.EXPORT_NAME]: opts.defaultExportName ?? '',
    [ExportConfigFormField.PERSONALIZATION_FIELDS]: [],
    [ExportConfigFormField.END_DATE]: '',
    [ExportConfigFormField.SYNC_END]: '',
    [ExportConfigFormField.VARIATION]: '',
    [ExportConfigFormField.EXPORT_FREQUENCY]: '',
    [ExportConfigFormField.AUDIENCE_EXPORT_CAMPAIGN_ID]: '',
    [ExportConfigFormField.AUDIENCE_SEGMENT_ID]: '',
    [ExportConfigFormField.AUDIENCE_FOLDER]: opts.defaultExportName ?? '',
    [ExportConfigFormField.API_TYPE]: SailthruApiType.JOBS_API,
    [ExportConfigFormField.EXPORT_INCLUSION_CRITERIA]:
      ExportInclusionCriteria.DELTAS,
    [ExportConfigFormField.SCHEDULE]: opts.includeSchedule
      ? getAudienceExportScheduleInitialState(CAMPAIGN_TYPE.ONGOING)
      : null,
    [ExportConfigFormField.ADDITIONAL_FIELDS]: {},
    [ExportConfigFormField.REP_NAME]: '',
    [ExportConfigFormField.CONTACT_LIST_NAME]: '',
    [ExportConfigFormField.USE_TYPE]: '',
    [ExportConfigFormField.BILLING_GROUP_ID]: '',
    [ExportConfigFormField.DESCRIPTION]: '',
    [ExportConfigFormField.TARGET_DELIVERY_DATE]: '',
    [ExportConfigFormField.SEND_DATE]: '',
    [ExportConfigFormField.VIANT_SEGMENT_NAME]: '',
    [ExportConfigFormField.VIANT_SEGMENT_ID]: '',
    [ExportConfigFormField.VIANT_SEGMENT_ID_TYPE]: '',
    [ExportConfigFormField.VIANT_AID]: '',
    [ExportConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED]: false,
    [ExportConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED]: false,
    [ExportConfigFormField.NEW_OR_EXISTING_CAMPAIGN]: 'New',
    [ExportConfigFormField.CAMPAIGN_ID]: '',
    [ExportConfigFormField.CAMPAIGN_NAME]: '',
    [ExportConfigFormField.MARKETING_CLOUD_BUSINESS_UNIT_ID]: '',
    [ExportConfigFormField.MARKETING_CLOUD_FOLDER]: '',
    [ExportConfigFormField.PULSE_POINT_ADVERTISER]: '',
    [ExportConfigFormField.PULSE_POINT_PRODUCT]: 'LIFE',
    [ExportConfigFormField.QUALTRICS_LIST_TYPE]: QualtricsListType.MAILING_LIST,
    [ExportConfigFormField.QUALTRICS_DIRECTORY_ID]: '',
    [ExportConfigFormField.MARKETO_AUDIENCE_TYPE]:
      MarketoAudienceType.STATIC_LIST,
    [ExportConfigFormField.MARKETO_CUSTOM_OBJECT]: '',
    [ExportConfigFormField.DV360_APP_ID]: '',
    [ExportConfigFormField.GOOGLE_ADS_APP_ID]: '',
    [ExportConfigFormField.AAM_DATA_SOURCE_ID]: '',
    [ExportConfigFormField.AEP_DATASET_ID]: '',
    [ExportConfigFormField.AEP_CUSTOM_NAMESPACE]: '',
    [ExportConfigFormField.TABLE_NAME]: opts.defaultTableName ?? '',
    [ExportConfigFormField.USER_CONSENT]: opts.userConsent ?? '',
    [ExportConfigFormField.USER_DATA_CONSENT]: opts.userDataConsent ?? '',
    [ExportConfigFormField.USER_PERSONALIZATION_CONSENT]:
      opts.userPersonalizationConsent ?? '',
    [ExportConfigFormField.ACTIVATED_DESTINATIONS]: [],
    [ExportConfigFormField.LIMIT]: 0,
    [ExportConfigFormField.SORT_BY]: 'sign up date',
    [ExportConfigFormField.SORT_DIRECTION]: 'Descending',
    [ExportConfigFormField.SELECTED_DATASET]: 0,
  };
}

export function getAudienceExportScheduleInitialState(
  campaignType: CAMPAIGN_TYPE,
  schedule?: AudienceExportSchedule
): AudienceExportSchedule {
  if (schedule) return schedule;
  return {
    schedule_type: campaignType,
    frequency: AudienceFrequencyEnum.DAILY,
    occurrence: [
      {
        type: ByType.WEEK_DAY,
        values: [
          WeekdaysEnum.MONDAY,
          WeekdaysEnum.TUESDAY,
          WeekdaysEnum.WEDNESDAY,
          WeekdaysEnum.THURSDAY,
          WeekdaysEnum.FRIDAY,
          WeekdaysEnum.SATURDAY,
          WeekdaysEnum.SUNDAY,
        ],
      },
    ],
    start_type: AudienceScheduleStartTypeEnum.NOW,
    start_time: null,
    end_type: AudienceScheduleEndTypeEnum.NEVER,
    end_time: null,
  };
}

/** Determines whether a given platform type is selected */
export function isPlatformTypeSelected(model: ExportFormModel, key: string) {
  return model.selectedDestination?.platform_type === key;
}

export type ExportFieldInputType =
  | 'input'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'data-extensions';

export type ExportFieldConfig = {
  inputType: ExportFieldInputType;
  placeholder: string;
  fieldKey: ExportConfigFormField;
  value: string;
  labelTitle: string;
  options?: FormSelectFieldOption[];
  hideWhen?: boolean;
  hint?: string;
  searchable?: boolean;
  disabled?: boolean;
};

export function createFieldConfigObject(
  inputType: ExportFieldInputType,
  placeholder: string,
  fieldKey: ExportConfigFormField,
  value: string,
  labelTitle: string,
  options?: FormSelectFieldOption[],
  hideWhen?: boolean,
  hint?: string,
  searchable?: boolean,
  disabled?: boolean
): ExportFieldConfig {
  return {
    inputType: inputType,
    placeholder: placeholder,
    labelTitle: labelTitle,
    fieldKey: fieldKey,
    value: value,
    options: options || [],
    hideWhen: hideWhen || false,
    hint: hint || '',
    searchable: searchable || false,
    disabled: disabled || false,
  };
}

type ResponseObject = {
  id: string;
  name: string;
};

export type DataExtensionsResponse = {data_extensions: ResponseObject[]};
export type BusinessUnitsResponse = {business_units: ResponseObject[]};
export type DataFoldersResponse = {data_folders: ResponseObject[]};
export type MarketoCustomObjectsResponse = {custom_objects: ResponseObject[]};
export type DestinationAudiencesResponse = {
  destination_audiences: ResponseObject[];
};
export type ActivatedDestinationsResponse = {
  activated_destinations: ResponseObject[];
};

async function fetchObjects<T>(url: string) {
  return fetcher<T>(url, {method: RequestMethod.GET});
}

export async function fetchDestinationDataExtensions(destinationId: string) {
  return fetchObjects<DataExtensionsResponse>(
    `/api/v2/destination_connections/${destinationId}/data_extensions`
  );
}

export async function fetchDestinationBusinessUnits(destinationId: string) {
  return fetchObjects<BusinessUnitsResponse>(
    `/api/v2/destination_connections/${destinationId}/business_units`
  );
}

export async function fetchDestinationDataFolders(destinationId: string) {
  return fetchObjects<DataFoldersResponse>(
    `/api/v2/destination_connections/${destinationId}/data_folders`
  );
}

export async function fetchMarketoCustomObjects(destinationId: string) {
  return fetchObjects<MarketoCustomObjectsResponse>(
    `/api/v2/destination_connections/${destinationId}/custom_objects`
  );
}

export async function fetchDestinationAudiences(destinationId: string) {
  return fetchObjects<DestinationAudiencesResponse>(
    `/api/v2/destination_connections/${destinationId}/destination_audiences`
  );
}

export async function fetchActivatedDestinations(
  destinationId: string,
  activatedDestinationsIds?: string[]
) {
  let url = `/api/v2/destination_connections/${destinationId}/activated_destinations`;
  if (activatedDestinationsIds?.length) {
    const params = new URLSearchParams();
    activatedDestinationsIds.forEach(id => {
      params.append('activated_destination_ids[]', id);
    });
    url += `?${params.toString()}`;
  }
  return fetchObjects<ActivatedDestinationsResponse>(url);
}

export function getConsentFromDataset(
  datasets: Dataset[],
  datasetId: number | undefined,
  consentType: ConsentType
) {
  if (!datasetId) return null;
  const dataset = datasets.find(({id}) => id === datasetId);
  const fieldMapping = dataset?.field_mapping;
  return fieldMapping?.[consentType];
}
export function renderSelectAllCheckboxes({
  labels,
}: {
  labels: {
    includeAllFields: string;
    includeGLFields: string;
  };
}) {
  return html`
    <div class="config__checkbox-with-tooltip--container">
      <div class="config__checkbox-with-tooltip">
        <fly-checkbox
          label=${labels.includeAllFields}
          @change=${(e: CustomEvent) => {
            createAndDispatchSelectAllEvent(
              ExportConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED,
              e.detail.checked,
              e.target
            );
          }}
        >
        </fly-checkbox>
        <fly-tooltip>
          <p slot="content" class="audience-exports__tooltip--content">
            ${DestinationLocale[locale.current].exportForm.additionalFields
              .includeAllFieldsTooltip}
          </p>
          <fly-icon size="sm" icon="info_outlined"></fly-icon>
        </fly-tooltip>
      </div>
      <div class="config__checkbox-with-tooltip">
        <fly-checkbox
          label=${labels.includeGLFields}
          @change=${(e: CustomEvent) => {
            createAndDispatchSelectAllEvent(
              ExportConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED,
              e.detail.checked,
              e.target
            );
          }}
        >
        </fly-checkbox>
        <fly-tooltip>
          <p slot="content" class="audience-exports__tooltip--content">
            ${DestinationLocale[locale.current].exportForm.additionalFields
              .glFieldsTooltip}
          </p>
          <fly-icon size="sm" icon="info_outlined"></fly-icon>
        </fly-tooltip>
      </div>
    </div>
  `;
}

export function renderV1SelectAllCheckboxes() {
  return renderSelectAllCheckboxes({
    labels: {
      includeAllFields:
        DestinationLocale[locale.current].exportForm.additionalFields
          .includeAllFieldsV1,
      includeGLFields:
        DestinationLocale[locale.current].exportForm.additionalFields
          .includeGLFieldsV1,
    },
  });
}

export function renderV2SelectAllCheckboxes() {
  return renderSelectAllCheckboxes({
    labels: {
      includeAllFields:
        DestinationLocale[locale.current].exportForm.additionalFields
          .includeAllFieldsV2,
      includeGLFields:
        DestinationLocale[locale.current].exportForm.additionalFields
          .includeGLFieldsV2,
    },
  });
}

export function createAndDispatchSelectAllEvent(
  fieldType: ExportConfigFormField,
  selected: boolean,
  target: EventTarget | null
) {
  const event = new CustomEvent(SELECT_ALL_FIELDS_EVENT, {
    detail: {
      fieldType,
      selected,
    },
    bubbles: true,
    composed: true,
  });
  target?.dispatchEvent(event);
}
